import React, { useState, useEffect, useMemo } from "react";
import "./Packages.scss";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import Loader from "../../components/Loader/Loader";
import Login from "../Login/Login";
import InspectionRequest from "../../components/InspectionRequest/InspectionRequest";
import { toast } from "react-toastify";

// Utility function to safely get localized text
const getLocalizedText = (obj, language, fallback = "") => {
  if (!obj) return fallback;
  return obj[language] || obj.en || obj.ar || fallback;
};

const Packages = ({ noPadding = false, className = "", previewPrice }) => {
  const [packageLists, setPackageLists] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [activePackageType, setActivePackageType] = useState("homes");

  const [selectedListForInspection, setSelectedListForInspection] =
    useState(null);

  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { addPackage, clearCart } = useCart();
  const { isAuthenticated } = useAuth();

  // Fetch package lists from API
  useEffect(() => {
    const fetchPackageLists = async () => {
      try {
        setLoading(true);
        const response = await api.get("/package-lists");

        if (response.data && response.data.status === "success") {
          // Ensure response.data.data is an array
          const packageListsData = Array.isArray(response.data.data)
            ? response.data.data
            : [];

          setPackageLists(packageListsData);

          // Extract all packages from all lists
          const allPackages = [];
          packageListsData.forEach((list) => {
            if (
              list &&
              list.packages &&
              Array.isArray(list.packages) &&
              list.packages.length > 0
            ) {
              list.packages.forEach((pkg) => {
                allPackages.push({
                  ...pkg,
                  packageType: getPackageTypeFromListName(list.name, list._id),
                  listId: list._id,
                  listName: list.name,
                });
              });
            }
          });

          setPackages(allPackages);
        } else {
          setError("Failed to fetch package lists data");
          console.error("Invalid API response structure:", response.data);
        }
      } catch (err) {
        setError("Error fetching package lists: " + err.message);
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPackageLists();
    clearCart();
  }, []);

  // Helper function to map list names to package types
  const getPackageTypeFromListName = (listName, listId) => {
    const nameEn = getLocalizedText(listName, "en", "").toLowerCase();
    const nameAr = getLocalizedText(listName, "ar", "");

    if (nameEn.includes("home") || nameAr.includes("منازل")) return "homes";
    if (nameEn.includes("compan") || nameAr.includes("شركات"))
      return "companies";
    if (nameEn.includes("open") || nameAr.includes("مفتوحة"))
      return "openPlaces";
    if (nameEn.includes("store") || nameAr.includes("محلات")) return "stores";
    if (
      nameEn.includes("cafe") ||
      nameEn.includes("restaurant") ||
      nameAr.includes("كافيهات") ||
      nameAr.includes("مطاعم")
    )
      return "cafesrestaurants";

    return listId; // fallback to using the list ID
  };

  // Define package types based on available lists
  const packageTypes = useMemo(() => {
    const types = [];

    // Ensure packageLists is an array before iterating
    if (Array.isArray(packageLists)) {
      packageLists.forEach((list) => {
        if (list && list.name) {
          const packageType = getPackageTypeFromListName(list.name, list._id);
          const nameEn = getLocalizedText(list.name, "en", "Unknown");
          const nameAr = getLocalizedText(list.name, "ar", "غير معروف");

          if (!types.find((type) => type.id === packageType)) {
            types.push({
              id: packageType,
              nameEn: nameEn,
              nameAr: nameAr,
              listId: list._id,
              hasPackages:
                list.packages &&
                Array.isArray(list.packages) &&
                list.packages.length > 0,
            });
          }
        }
      });
    }

    // Sort to put homes first
    types.sort((a, b) => {
      if (a.id === "homes") return -1;
      if (b.id === "homes") return 1;
      return 0;
    });

    return types;
  }, [packageLists]);

  // Check if there are monthly and annually packages
  const hasMonthlyPackages = useMemo(
    () =>
      Array.isArray(packages) && packages.some((pkg) => pkg.type === "monthly"),
    [packages]
  );

  const hasAnnuallyPackages = useMemo(
    () =>
      Array.isArray(packages) &&
      packages.some((pkg) => pkg.type === "annually"),
    [packages]
  );

  // Set the default active period based on package availability
  const [activePeriod, setActivePeriod] = useState(
    hasMonthlyPackages ? "monthly" : "annually"
  );

  // Update active period if packages change
  useEffect(() => {
    if (
      activePeriod === "monthly" &&
      !hasMonthlyPackages &&
      hasAnnuallyPackages
    ) {
      setActivePeriod("annually");
    } else if (
      activePeriod === "annually" &&
      !hasAnnuallyPackages &&
      hasMonthlyPackages
    ) {
      setActivePeriod("monthly");
    }
  }, [hasMonthlyPackages, hasAnnuallyPackages, activePeriod]);

  // Filter packages based on active period and package type
  const filteredPackages = useMemo(() => {
    if (!Array.isArray(packages)) return [];

    return packages.filter((pkg) => {
      // First filter by period type
      const periodMatch =
        activePeriod === "monthly"
          ? pkg.type === "monthly"
          : pkg.type === "annually";

      // Then filter by package type
      const packageTypeMatch = pkg.packageType === activePackageType;

      return periodMatch && packageTypeMatch;
    });
  }, [packages, activePeriod, activePackageType]);

  // Get the appropriate period text based on language and period type
  const getPeriodText = () => {
    return language === "en"
      ? activePeriod === "monthly"
        ? "month"
        : "year"
      : activePeriod === "monthly"
      ? "شهر"
      : "سنة";
  };

  // Add display properties to packages
  const enhancedPackages = useMemo(() => {
    if (!Array.isArray(filteredPackages) || filteredPackages.length === 0) {
      return [];
    }

    const popularPackageIndex = filteredPackages.findIndex(
      (pkg) => pkg.popular
    );
    const fallbackPopularIndex = filteredPackages.length > 0 ? 0 : -1;

    return filteredPackages.map((pkg, index) => {
      const isPopular =
        popularPackageIndex !== -1
          ? index === popularPackageIndex
          : index === fallbackPopularIndex;

      // Convert saving to number and check if it's greater than 0
      const savingAmount = Number(pkg.saving) || 0;
      const hasSaving = savingAmount > 0;

      return {
        ...pkg,
        id: pkg._id,
        popular: pkg.popular,
        color: isPopular ? "#4CAF50" : "",
        period: getPeriodText(),
        saving: hasSaving
          ? language === "en"
            ? `Save ${savingAmount}`
            : `وفر ${savingAmount} ريال`
          : null,
        savingAmount: savingAmount, // Store the numeric value for reference
      };
    });
  }, [filteredPackages, language, activePeriod]);

  // Handle login modal close
  const handleLoginClose = () => {
    setShowLoginModal(false);
    if (isAuthenticated && selectedPackage) {
      processPackageSubscription(selectedPackage);
      setSelectedPackage(null);
    }
  };

  // Process package subscription
  const processPackageSubscription = (pkg) => {
    const packageData = {
      _id: pkg._id,
      id: pkg._id,
      name: {
        en: getLocalizedText(pkg.name, "en", "Package"),
        ar: getLocalizedText(pkg.name, "ar", "باقة"),
      },
      price: pkg.price,
      type: "package",
      period: activePeriod,
      numberOfVisits: pkg.numberOfVisits,
      dailyMaximum: pkg.dailyMaximum,
      features: pkg.features,
      packageType: pkg.packageType,
      listId: pkg.listId,
    };

    addPackage(packageData);
    navigate("/cart");
  };

  // Handle subscription package selection
  const handleSubscribePackage = (pkg) => {
    if (!isAuthenticated) {
      setSelectedPackage(pkg);
      setShowLoginModal(true);
      localStorage.setItem("pendingPackage", JSON.stringify(pkg));
      return;
    }
    processPackageSubscription(pkg);
  };

  // Handle changing the active package type
  const handlePackageTypeChange = (typeId) => {
    setActivePackageType(typeId);

    // Check if selected type has no packages and should show inspection request
    const selectedType = packageTypes.find((type) => type.id === typeId);
    if (selectedType && !selectedType.hasPackages) {
      setSelectedListForInspection(selectedType);
    } else {
      setSelectedListForInspection(null);
    }
  };

  // Handle inspection request submission
  const handleInspectionRequest = async (inspectionData) => {
    try {
      const requestData = {
        list: selectedListForInspection.listId,
        address: inspectionData.address,
        date: inspectionData.date,
      };

      await api.post("/preview-requests", requestData);

      toast.success(
        language === "en"
          ? "Your inspection request has been created successfully! We will contact you 24 hours before your scheduled appointment."
          : "تم إنشاء طلب المعاينة بنجاح وسوف يتم التواصل معك قبل الموعد المحدد بـ 24 ساعة"
      );
    } catch (error) {
     toast.error(error.message)

    }
  };

  // Close inspection request modal
  const handleCloseInspectionRequest = () => {
    setSelectedListForInspection(null);
    setActivePackageType("homes");
  };

  // Check if current selection should show inspection request
  const shouldShowInspectionRequest = useMemo(() => {
    const selectedType = packageTypes.find(
      (type) => type.id === activePackageType
    );
    return selectedType && !selectedType.hasPackages;
  }, [activePackageType, packageTypes]);

  return (
    <div
      className={`subscription-section ${
        noPadding ? "no-padding" : ""
      } ${className}`}
    >
      <div className="container">
        <h2 className="section-title">{t("subscriptionPackages")}</h2>
        <p className="section-description">{t("chooseSubscriptionPackage")}</p>

        {/* Package Type Filter */}
        <div className="package-types-container">
          <div className="package-types-wrapper">
            {packageTypes.map((type) => (
              <div
                key={type.id}
                className={`package-type-item ${
                  activePackageType === type.id ? "active" : ""
                }`}
                onClick={() => handlePackageTypeChange(type.id)}
              >
                <span className="package-type-text">
                  {language === "en" ? type.nameEn : type.nameAr}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Period Toggle - Only show if both types of packages are available */}
        {hasMonthlyPackages &&
          hasAnnuallyPackages &&
          !shouldShowInspectionRequest && (
            <div className="period-toggle">
              <button
                className={`period-btn ${
                  activePeriod === "monthly" ? "active" : ""
                }`}
                onClick={() => setActivePeriod("monthly")}
              >
                {language === "en" ? "Monthly" : "شهري"}
              </button>
              <button
                className={`period-btn ${
                  activePeriod === "annually" ? "active" : ""
                }`}
                onClick={() => setActivePeriod("annually")}
              >
                {language === "en" ? "Annual" : "سنوي"}
              </button>
            </div>
          )}

        {/* Loading State */}
        {loading && <Loader />}

        {/* Error State */}
        {!loading && error && (
          <div className="error">
            {language === "en" ? "Error: " : "خطأ: "}
            {error}
          </div>
        )}

        {/* Show Inspection Request if no packages available for selected type */}
        {!loading &&
          !error &&
          shouldShowInspectionRequest &&
          selectedListForInspection && (
            <InspectionRequest
              previewPrice={previewPrice}
              packageType={activePackageType}
              listId={selectedListForInspection.listId}
              listName={{
                nameEn: selectedListForInspection.nameEn,
                nameAr: selectedListForInspection.nameAr,
                name: {
                  en: selectedListForInspection.nameEn,
                  ar: selectedListForInspection.nameAr,
                },
              }}
              listDescription={{
                en:
                  (Array.isArray(packageLists) &&
                    packageLists.find(
                      (list) => list._id === selectedListForInspection.listId
                    )?.description?.en) ||
                  "",
                ar:
                  (Array.isArray(packageLists) &&
                    packageLists.find(
                      (list) => list._id === selectedListForInspection.listId
                    )?.description?.ar) ||
                  "",
              }}
              onInspectionRequest={handleInspectionRequest}
              onClose={handleCloseInspectionRequest}
              requiresAddress={true}
            />
          )}

        {/* Show packages if available */}
        {!loading &&
          !error &&
          !shouldShowInspectionRequest &&
          enhancedPackages.length > 0 && (
            <div className="packages-grid">
              {enhancedPackages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`package-card ${pkg.popular ? "popular" : ""}`}
                  style={pkg.popular ? { borderColor: pkg.color } : {}}
                >
                  {pkg.popular && (
                    <div
                      className="popular-badge"
                      style={{ backgroundColor: pkg.color }}
                    >
                      {language === "en" ? "Most Popular" : "الأكثر طلباً"}
                    </div>
                  )}
                  <div className="package-header">
                    <h3 className="package-name">
                      {getLocalizedText(pkg.name, language)}
                    </h3>
                    {pkg.packageType && (
                      <div className="package-type-badge">
                        {language === "en"
                          ? packageTypes.find(
                              (type) => type.id === pkg.packageType
                            )?.nameEn || pkg.packageType
                          : packageTypes.find(
                              (type) => type.id === pkg.packageType
                            )?.nameAr || pkg.packageType}
                      </div>
                    )}
                    <div className="package-price">
                      <span className="currency">
                        {language === "en" ? "SAR" : "ر.س"}
                      </span>
                      <span className="amount">{pkg.price.toFixed(2)}</span>
                      <span className="period">/{pkg.period}</span>
                    </div>

                    {pkg.saving && pkg.savingAmount > 0 && (
                      <div className="saving-badge">{pkg.saving}</div>
                    )}

                    <p className="package-description">
                      {getLocalizedText(pkg.description, language)}
                    </p>
                  </div>

                  {pkg.features &&
                    Array.isArray(pkg.features) &&
                    pkg.features.length > 0 && (
                      <ul className="features-list">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="feature-item">
                            <span className="feature-check">✓</span>
                            {getLocalizedText(feature, language)}
                          </li>
                        ))}
                      </ul>
                    )}

                  <button
                    className={`subscribe-btn ${
                      pkg.popular ? "fill" : "outlined"
                    }`}
                    style={pkg.popular ? { backgroundColor: pkg.color } : {}}
                    onClick={() => handleSubscribePackage(pkg)}
                  >
                    {language === "en" ? "Subscribe Now" : "اشترك الآن"}
                  </button>
                </div>
              ))}
            </div>
          )}

        {/* Show message if no packages available */}
        {!loading &&
          !error &&
          !shouldShowInspectionRequest &&
          enhancedPackages.length === 0 && (
            <div className="no-packages-message">
              <p>
                {language === "en"
                  ? "No packages available for this category."
                  : "لا توجد باقات متاحة لهذه الفئة."}
              </p>
            </div>
          )}
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <Login open={showLoginModal} onClose={handleLoginClose} />
      )}
    </div>
  );
};

export default Packages;
